import {useFullSession, useOptionalSession} from '@aboutyou/user';
import {useEffect, useRef} from 'react';

/**
 * Should never keep product data for product id changes but should use previous data in case of other updates, e.g. to the session to reduce flickering while on the same ADP.
 * initial value -> `true`
 * `productId` changes -> `false`
 * `session` changes -> `true`
 */
export const useKeepPreviousData = ({
  productId,
  session,
}: {
  productId: number | undefined;
  session:
    | ReturnType<typeof useOptionalSession>
    | ReturnType<typeof useFullSession>;
}) => {
  const hashRef = useRef(productId);

  /**
   * The `keepPreviousData` value should be true while rerenders happen for a given productId. ex: when `session` changes.
   * And when switched to a new productId, the value should be false for the first render cycle so that we show the skeletons immedietly.
   * This is why set the `hashRef` to the last `productId` we have in the cleanup function.
   * But the next render cycles will have the `hashRef` set to the new `productId` and this makes the `keepPreviousData` to be `true` for the next render cycles.
   */
  useEffect(() => {
    return () => {
      hashRef.current = productId;
    };
  }, [productId, session]);

  return hashRef.current === productId;
};
